import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { reportsKey } from '@/features/mgmt/data';
import { tasksKey } from '@/features/tasks/data';
import { supabase } from '@/lib/supabase';

/** Screens to refresh when someone else (often FarmAI) changes these tables. */
const LIVE: Record<string, readonly (readonly string[])[]> = {
  shift_plans: [['shift_plan']],
  shift_rows: [['shift_plan']],
  tasks: [tasksKey],
  reports: [reportsKey],
};

/**
 * Follows changes through Supabase Realtime, which applies each user's policies. A whole-day
 * shift save arrives as one event per row, so refreshes are bunched.
 */
export function useLiveRefresh() {
  const qc = useQueryClient();
  const uid = useAuth().session?.user.id;
  useEffect(() => {
    if (!uid) return;
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const refresh = (key: readonly string[]) => {
      const id = key.join('/');
      clearTimeout(timers.get(id));
      timers.set(
        id,
        setTimeout(() => void qc.invalidateQueries({ queryKey: key }), 400),
      );
    };
    const channel = supabase.channel('farmos-live');
    for (const [table, keys] of Object.entries(LIVE))
      channel.on('postgres_changes', { event: '*', schema: 'public', table }, () =>
        keys.forEach(refresh),
      );
    channel.subscribe();
    return () => {
      timers.forEach(clearTimeout);
      void supabase.removeChannel(channel);
    };
  }, [qc, uid]);
}
